import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { canonicalLinkforgePath } from "./lib/forgelinkPathway";
import AppShell from "./components/AppShell";
import Toasts from "./components/Toasts";
import EditRequestsPage from "./features/editMode/EditRequestsPage";
import { useAuth } from "./lib/authStore";
import AnalyticsPage from "./pages/AnalyticsPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ArtifactsPage from "./pages/ArtifactsPage";
import BrainPage from "./pages/BrainPage";
import BuildLincPage from "./pages/BuildLincPage";
import BusinessDetailPage from "./pages/BusinessDetailPage";
import BusinessesPage from "./pages/BusinessesPage";
import BusinessWorkspacePage from "./pages/BusinessWorkspacePage";
import CommunicationsPage from "./pages/CommunicationsPage";
import CostPage from "./pages/CostPage";
import DarylPilotPage from "./pages/DarylPilotPage";
import ForgeLinkDesignDesk from "./pages/ForgeLinkDesignDesk";
import DashboardPage from "./pages/DashboardPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import FocusPage from "./pages/FocusPage";
import FocusBentoPage from "./pages/FocusBentoPage";
import LincWerksPrimaryPage from "./pages/LincWerksPrimaryPage";
import LincWerksRedesignPreviewPage from "./pages/LincWerksRedesignPreviewPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import LedgerPage from "./pages/LedgerPage";
import LoginPage from "./pages/LoginPage";
import MissionDetailPage from "./pages/MissionDetailPage";
import MissionsListPage from "./pages/MissionsListPage";
import NotFoundPage from "./pages/NotFoundPage";
import ProjectGithubPage from "./pages/ProjectGithubPage";
import PublicHomePage from "./pages/PublicHomePage";
import QaCenterPage from "./pages/QaCenterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SettingsPage from "./pages/SettingsPage";
import SupportPage from "./pages/SupportPage";
import VentureStudioPage from "./pages/VentureStudioPage";
import "./pages/BusinessWorkspaceForms.css";

// The local visual-edit dev server can return 404 for direct React Router
// paths. Founder Dashboard opens the always-served root with an `open` query;
// promote that path before BrowserRouter initializes, then remove the query.
const startupRoute = new URLSearchParams(window.location.search).get("open");
if (startupRoute?.startsWith("/") && !startupRoute.startsWith("//")) {
  window.history.replaceState(window.history.state, "", canonicalLinkforgePath(startupRoute));
}

function ForgeLincAliasRedirect() {
  const location = useLocation();
  return <Navigate to={canonicalLinkforgePath(`${location.pathname}${location.search}${location.hash}`)} replace />;
}

function ResetGate({ children }) {
  const { user, scope, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (scope !== "reset_only") return <Navigate to="/focus" replace />;
  return children;
}

function LoginGate({ children }) {
  const { user, scope, ready } = useAuth();
  if (!ready) return null;
  if (user && scope === "reset_only") return <Navigate to="/reset-password" replace />;
  if (user) {
    // Phase 5b: honor ?next= so an already-authenticated user bounced here
    // by the ForgeLinc nginx bridge still lands on the intended page.
    const raw = new URLSearchParams(window.location.search).get("next");
    if (raw && raw.startsWith("/") && !raw.startsWith("//")) {
      window.location.assign(raw);
      return null;
    }
    return <Navigate to="/focus" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicHomePage />} />
      <Route path="/public.html" element={<PublicHomePage />} />
      <Route path="/login" element={<LoginGate><LoginPage /></LoginGate>} />
      <Route path="/reset-password" element={<ResetGate><ResetPasswordPage /></ResetGate>} />
      <Route element={<AppShell />}>
        <Route path="/focus" element={<LincWerksRedesignPreviewPage />} />
        <Route path="/focus/redesign" element={<Navigate to="/focus" replace />} />
        <Route path="/focus/queues" element={<LincWerksPrimaryPage />} />
        <Route path="/focus/bento" element={<FocusBentoPage />} />
        <Route path="/focus/classic" element={<FocusPage />} />
        <Route path="/businesses" element={<BusinessesPage />} />
        <Route path="/businesses/gearlinc" element={<Navigate to="/gearlinc" replace />} />
        <Route path="/businesses/linkforge" element={<Navigate to="/linkforge" replace />} />
        <Route path="/businesses/:key" element={<BusinessDetailPage />} />
        <Route path="/gearlinc" element={<BusinessWorkspacePage forcedKey="gearlinc" />} />
        <Route path="/linkforge" element={<BusinessWorkspacePage forcedKey="linkforge" />} />
        <Route path="/linkforge/design" element={<ForgeLinkDesignDesk />} />
        <Route path="/linkforge/daryl-pilot" element={<DarylPilotPage />} />
        <Route path="/lincforge" element={<ForgeLincAliasRedirect />} />
        <Route path="/lincforge/*" element={<ForgeLincAliasRedirect />} />
        <Route path="/forgelinc" element={<ForgeLincAliasRedirect />} />
        <Route path="/forgelinc/*" element={<ForgeLincAliasRedirect />} />
        <Route path="/commandlinc" element={<DashboardPage />} />
        <Route path="/commandlinc/venture-studio" element={<VentureStudioPage />} />
        <Route path="/commandlinc/buildlinc" element={<BuildLincPage />} />
        <Route path="/commandlinc/communications" element={<CommunicationsPage />} />
        <Route path="/commandlinc/missions" element={<MissionsListPage />} />
        <Route path="/commandlinc/missions/:id" element={<MissionDetailPage />} />
        <Route path="/commandlinc/approvals" element={<ApprovalsPage />} />
        <Route path="/commandlinc/qa" element={<QaCenterPage />} />
        <Route path="/commandlinc/workforce" element={<DepartmentsPage />} />
        <Route path="/commandlinc/artifacts" element={<ArtifactsPage />} />
        <Route path="/commandlinc/brain" element={<BrainPage />} />
        <Route path="/commandlinc/cost" element={<CostPage />} />
        <Route path="/commandlinc/ledger" element={<LedgerPage />} />
        <Route path="/commandlinc/analytics" element={<AnalyticsPage />} />
        <Route path="/commandlinc/integrations" element={<IntegrationsPage />} />
        <Route path="/commandlinc/settings" element={<SettingsPage />} />
        <Route path="/commandlinc/support" element={<SupportPage />} />
        <Route path="/commandlinc/edit-requests" element={<EditRequestsPage />} />
        <Route path="/projects/:id/github" element={<ProjectGithubPage />} />
        <Route path="/dashboard" element={<Navigate to="/commandlinc" replace />} />
        <Route path="/missions" element={<Navigate to="/commandlinc/missions" replace />} />
        <Route path="/missions/:id" element={<LegacyMissionRedirect />} />
        <Route path="/approvals" element={<Navigate to="/commandlinc/approvals" replace />} />
        <Route path="/qa" element={<Navigate to="/commandlinc/qa" replace />} />
        <Route path="/departments" element={<Navigate to="/commandlinc/workforce" replace />} />
        <Route path="/artifacts" element={<Navigate to="/commandlinc/artifacts" replace />} />
        <Route path="/brain" element={<Navigate to="/commandlinc/brain" replace />} />
        <Route path="/cost" element={<Navigate to="/commandlinc/cost" replace />} />
        <Route path="/ledger" element={<Navigate to="/commandlinc/ledger" replace />} />
        <Route path="/integrations" element={<Navigate to="/commandlinc/integrations" replace />} />
        <Route path="/settings" element={<Navigate to="/commandlinc/settings" replace />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function LegacyMissionRedirect() {
  const path = window.location.pathname;
  const m = path.match(/\/missions\/(.+)$/);
  return <Navigate to={m ? `/commandlinc/missions/${m[1]}` : "/commandlinc/missions"} replace />;
}

export default function App() {
  const hydrate = useAuth((s) => s.hydrate);
  useEffect(() => { hydrate(); }, [hydrate]);
  return <BrowserRouter><AppRoutes /><Toasts /></BrowserRouter>;
}
